import { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsPlaces } from '../utils/googleMapsLoader';

export default function useGooglePlacesAutocomplete({
	onPlaceSelected,
	options = {},
	enabled = true,
	apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY,
}) {
	const inputElementRef = useRef(null);
	const autocompleteRef = useRef(null);
	const placeSelectedRef = useRef(onPlaceSelected);
	const optionsRef = useRef(options);
	const [autocompleteError, setAutocompleteError] = useState('');

	useEffect(() => {
		placeSelectedRef.current = onPlaceSelected;
	}, [onPlaceSelected]);

	useEffect(() => {
		optionsRef.current = options;
	}, [options]);

	useEffect(() => {
		if (!enabled || !inputElementRef.current) {
			return undefined;
		}

		let isCancelled = false;
		let mapsListener = null;

		loadGoogleMapsPlaces(apiKey)
			.then((google) => {
				if (isCancelled || !inputElementRef.current) {
					return;
				}

				setAutocompleteError('');

				autocompleteRef.current = new google.maps.places.Autocomplete(
					inputElementRef.current,
					{
						fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'name'],
						types: ['address'],
						...optionsRef.current,
					}
				);

				mapsListener = autocompleteRef.current.addListener('place_changed', () => {
					const place = autocompleteRef.current?.getPlace?.();
					placeSelectedRef.current?.(place);
				});
			})
			.catch((error) => {
				if (!isCancelled) {
					setAutocompleteError(
						error?.message || 'Google address lookup is unavailable right now.'
					);
				}
			});

		return () => {
			isCancelled = true;

			if (mapsListener?.remove) {
				mapsListener.remove();
			}

			if (autocompleteRef.current && window.google?.maps?.event) {
				window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
			}

			autocompleteRef.current = null;
		};
	}, [apiKey, enabled]);

	const attachInputRef = (node) => {
		inputElementRef.current = node;
	};

	return {
		attachInputRef,
		autocompleteError,
	};
}
